import { useCallback, useEffect, useState } from 'react';
import { TILES_PRECACHE_NAME, TILES_OFFLINE_PRECACHE_NAME } from '../../config/precache';
import { useNetwork } from './useNetwork';

// Both the bucket mapbox-gl fills itself and the one the service worker fills
// for us (see service-worker.js) need their expirations pushed out offline so
// mapbox-gl doesn't reject the cached tiles as stale.
const TILE_CACHE_NAMES = [TILES_PRECACHE_NAME, TILES_OFFLINE_PRECACHE_NAME];

export const useTilesCache = () => {
  const [loaded, setLoaded] = useState(false);

  const networkAvailable = useNetwork();

  const refreshTilesCache = useCallback(async () => {
    const now = new Date();
    const refreshOne = async (cacheName) => {
      // Only our own bucket uses query-less keys (see service-worker.js); the
      // `mapbox-tiles` bucket belongs to mapbox-gl, which looks entries up by
      // full URL — rewriting its keys would break the library's own cache.
      const normalizeKeys = cacheName === TILES_OFFLINE_PRECACHE_NAME;
      const cache = await caches.open(cacheName);
      const requests = await cache.keys();
      await Promise.all(
        requests.map(async (request) => {
          const response = await cache.match(request, { ignoreVary: true });
          const clonedReponse = response.clone();
          // add 24 hours for good measure
          const newExpiration = new Date(now.getTime() + 86400 * 1000);
          clonedReponse.headers.set('Expires', newExpiration.toGMTString());
          clonedReponse.headers.set(
            'Cache-Control',
            'max-age=2147483648,s-maxage=2147483648,stale-while-revalidate=2147483648,stale-if-error=2147483648'
          );
          const keyUrl = new URL(request.url);
          if (normalizeKeys && keyUrl.search) {
            // legacy entry keyed with sku/access_token: converge it (and any
            // duplicate of the same tile) onto the normalized key
            keyUrl.search = '';
            await cache.put(keyUrl.href, clonedReponse);
            await cache.delete(request); // exact match: removes only the legacy key
          } else {
            await cache.put(request, clonedReponse);
          }
          console.info('Tile cache refreshed', request.url);
        })
      );
    };
    await Promise.all(TILE_CACHE_NAMES.map(refreshOne));
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!networkAvailable) refreshTilesCache();
    else setLoaded(true);
  }, []);

  return loaded;
};
